import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import PlanList from "@/components/subscription/PlanList";
import PlanComparisonTable from "@/components/subscription/PlanComparisonTable";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ensureMidtransSnapReady } from "@/utils/midtrans";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function Pricing() {
  const { user, checkAuth } = useAuth();
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlanId, setProcessingPlanId] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [annualEnabled, setAnnualEnabled] = useState(false);
  const [annualDiscountMode, setAnnualDiscountMode] = useState("percent");
  const [annualDiscountValue, setAnnualDiscountValue] = useState(0);
  const [creditPacks, setCreditPacks] = useState([]);
  const [creditPacksLoading, setCreditPacksLoading] = useState(false);
  const [buyingPackId, setBuyingPackId] = useState(null);
  const locale = i18n.language?.startsWith("en") ? "en-US" : "id-ID";
  const currentPlan = user?.plan || "free";
  const trialEndDate = user?.trial_end ? new Date(user.trial_end) : null;
  const isTrialActive =
    !!trialEndDate &&
    !Number.isNaN(trialEndDate.getTime()) &&
    trialEndDate > new Date() &&
    currentPlan !== "free";
  const canBuyCredits = currentPlan !== "free" && !isTrialActive;

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/subscription/plans`, {
        withCredentials: true,
      });
      const planItems = Array.isArray(res.data) ? res.data : [];
      setPlans(planItems);

      const annualPlan = planItems.find((plan) => Boolean(plan?.annual_enabled));
      const annualIsEnabled = Boolean(annualPlan);
      setAnnualEnabled(annualIsEnabled);
      setAnnualDiscountMode(String(annualPlan?.annual_discount_mode || "percent"));
      setAnnualDiscountValue(Number(annualPlan?.annual_discount_value || 0));
      if (!annualIsEnabled) {
        setBillingCycle("monthly");
      }
    } catch (err) {
      setPlans([]);
      toast.error(
        err?.response?.data?.detail ||
          t("pricing.loadFailed", { defaultValue: "Failed to load plans" }),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadCreditPacks = useCallback(async () => {
    setCreditPacksLoading(true);
    try {
      const res = await axios.get(`${API}/credits/packages`, {
        withCredentials: true,
      });
      setCreditPacks(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setCreditPacks([]);
      toast.error(
        err?.response?.data?.detail ||
          t("dashboard.credits.loadFailed", {
            defaultValue: "Gagal memuat paket kredit",
          }),
      );
    } finally {
      setCreditPacksLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    if (canBuyCredits) {
      loadCreditPacks();
    } else {
      setCreditPacks([]);
      setCreditPacksLoading(false);
    }
  }, [canBuyCredits, loadCreditPacks]);

  const discountBadgeLabel = useMemo(() => {
    if (!annualEnabled || annualDiscountValue <= 0) return null;
    if (annualDiscountMode === "flat") {
      return t("pricing.billing.discountInfoNominal", {
        defaultValue: "Diskon tahunan Rp {{value}}",
        value: Number(annualDiscountValue).toLocaleString(locale),
      });
    }
    return t("pricing.billing.discountInfoPercent", {
      defaultValue: "Diskon tahunan {{value}}%",
      value: annualDiscountValue,
    });
  }, [annualDiscountMode, annualDiscountValue, annualEnabled, locale, t]);

  const handleCheckout = useCallback(
    async (plan) => {
      const planId = plan?.id;
      if (!planId || planId === currentPlan) return;

      setProcessingPlanId(planId);
      try {
        const res = await axios.post(
          `${API}/subscription/checkout`,
          { plan_id: planId, billing_cycle: annualEnabled ? billingCycle : "monthly" },
          { withCredentials: true },
        );
        const payload = res?.data || {};

        if (payload.downgrade_scheduled) {
          const effectiveLabel = payload.effective_at
            ? new Date(payload.effective_at).toLocaleDateString(locale, {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
            : t("common.soon", { defaultValue: "soon" });
          toast.success(
            t("subscription.pendingDowngrade", {
              plan: t(`dashboard.plan.label.${payload.new_plan}`, {
                defaultValue: payload.new_plan,
              }),
              date: effectiveLabel,
            }),
          );
          await checkAuth();
          await loadPlans();
          setProcessingPlanId(null);
          return;
        }

        if (payload.trial === true && payload.requires_confirmation) {
          if (!payload.order_id) {
            toast.error(t("common.error"));
            return;
          }
          await axios.post(
            `${API}/subscription/trial/confirm`,
            { order_id: payload.order_id },
            { withCredentials: true },
          );
          toast.success(
            t("dashboard.payment.trialActive", {
              defaultValue: "Trial activated successfully",
            }),
          );
          window.location.href = `/dashboard?payment=success&order_id=${encodeURIComponent(
            payload.order_id,
          )}`;
          return;
        }

        if (payload.snap_token) {
          try {
            await ensureMidtransSnapReady();
          } catch {
            toast.error(
              t("pricing.snapUnavailable", {
                defaultValue: "Payment popup unavailable. Please try again.",
              }),
            );
            return;
          }

          window.snap.pay(payload.snap_token, {
            onSuccess: () => {
              window.location.href = `/dashboard?payment=pending&order_id=${encodeURIComponent(
                payload.order_id,
              )}`;
            },
            onPending: () => {
              window.location.href = `/dashboard?payment=pending&order_id=${encodeURIComponent(
                payload.order_id,
              )}`;
            },
            onError: () => {
              window.location.href = `/dashboard?payment=failed&order_id=${encodeURIComponent(
                payload.order_id,
              )}`;
            },
            onClose: () => {
              toast.info(
                t("pricing.paymentCancelled", {
                  defaultValue: "Pembayaran dibatalkan",
                }),
              );
              setProcessingPlanId(null);
            },
          });
          return;
        }

        if (payload.redirect_url) {
          window.location.href = payload.redirect_url;
          return;
        }

        toast.error(
          t("pricing.snapUnavailable", {
            defaultValue: "Payment popup unavailable. Please try again.",
          }),
        );
      } catch (err) {
        toast.error(err?.response?.data?.detail || t("common.error"));
      } finally {
        setProcessingPlanId(null);
      }
    },
    [annualEnabled, billingCycle, checkAuth, currentPlan, loadPlans, locale, t],
  );

  const handleBuyCredits = useCallback(
    async (packId) => {
      if (!packId || !canBuyCredits) return;
      setBuyingPackId(packId);
      try {
        const res = await axios.post(
          `${API}/credits/purchase`,
          { pack_id: packId },
          { withCredentials: true },
        );

        if (res.data.snap_token) {
          try {
            await ensureMidtransSnapReady();
          } catch {
            toast.error(
              t("pricing.snapUnavailable", {
                defaultValue: "Payment popup unavailable. Please try again.",
              }),
            );
            return;
          }
          window.snap.pay(res.data.snap_token, {
            onSuccess: () => {
              window.location.href = `/dashboard?credits=success&order_id=${encodeURIComponent(
                res.data.order_id,
              )}`;
            },
            onPending: () => {
              toast.info(
                t("dashboard.credits.pending", {
                  defaultValue: "Pembelian kredit sedang diproses",
                }),
              );
              window.location.href = `/dashboard?credits=pending&order_id=${encodeURIComponent(
                res.data.order_id,
              )}`;
            },
            onError: () => {
              toast.error(t("dashboard.credits.failed"));
              setBuyingPackId(null);
            },
            onClose: () => {
              setBuyingPackId(null);
            },
          });
          return;
        }

        if (res.data.redirect_url) {
          window.location.href = res.data.redirect_url;
          return;
        }

        toast.error(
          t("pricing.snapUnavailable", {
            defaultValue: "Payment popup unavailable. Please try again.",
          }),
        );
      } catch (err) {
        toast.error(
          err?.response?.data?.detail ||
            t("dashboard.credits.failed", {
              defaultValue: "Gagal memproses pembelian kredit",
            }),
        );
      } finally {
        setBuyingPackId(null);
      }
    },
    [canBuyCredits, t],
  );

  return (
    <main className="min-h-screen" data-testid="app-pricing-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("nav.dashboard")}
        </Link>

        <div className="mb-6">
          <div>
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              {t("nav.pricing")}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">
              {t("pricing.title")}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t("pricing.appSubtitle", {
                defaultValue:
                  "Pilih paket untuk akun Anda. Paket aktif ditandai otomatis.",
              })}
            </p>
          </div>
          {annualEnabled && discountBadgeLabel && (
            <div className="mt-5 flex justify-center">
              <div className="inline-flex flex-wrap items-center justify-center gap-2">
                <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1">
                <button
                  type="button"
                  className={`rounded-full px-3 py-1 text-sm transition-colors ${
                    billingCycle === "monthly"
                      ? "bg-brand-primary text-white"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => setBillingCycle("monthly")}
                  data-testid="pricing-billing-monthly"
                >
                  {t("pricing.billing.monthly", { defaultValue: "Bulanan" })}
                </button>
                <button
                  type="button"
                  className={`rounded-full px-3 py-1 text-sm transition-colors ${
                    billingCycle === "annual"
                      ? "bg-brand-primary text-white"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  onClick={() => setBillingCycle("annual")}
                  data-testid="pricing-billing-annual"
                >
                  {t("pricing.billing.annual", { defaultValue: "Tahunan" })}
                </button>
              </div>
                <span
                  className={`text-[11px] font-mono uppercase px-2.5 py-1 rounded-sm border border-amber-300/70 bg-amber-300/90 text-slate-900 transition-opacity ${
                    billingCycle === "annual" ? "opacity-100" : "opacity-45"
                  }`}
                >
                  {discountBadgeLabel}
                </span>
              </div>
            </div>
          )}
        </div>

        <PlanList
          plans={plans}
          currentPlan={currentPlan}
          loading={loading}
          processingPlanId={processingPlanId}
          onPlanSelect={handleCheckout}
          billingCycle={annualEnabled ? billingCycle : "monthly"}
        />

        {!loading && plans.length > 0 && (
          <div className="mt-6 w-full">
            <PlanComparisonTable
              plans={plans}
              currentPlan={currentPlan}
              processingPlanId={processingPlanId}
              onPlanSelect={handleCheckout}
            />
          </div>
        )}

        {canBuyCredits && (
          <section className="mt-6 rounded-md border border-border bg-card/40 p-4 md:p-5" data-testid="pricing-credit-section">
            <h2 className="font-heading text-xl md:text-2xl font-bold">
              {t("dashboard.credits.title")}
            </h2>
            <p className="text-sm md:text-base text-muted-foreground mt-1">
              {t("pricing.credits.subtitle", {
                defaultValue:
                  "Lanjutkan analisis saat kuota habis. Foto 1 kredit, Audio 2 kredit, Video 3 kredit.",
              })}
            </p>

            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              <div className="rounded-sm border border-border bg-secondary/20 px-3 py-2 text-xs text-muted-foreground">
                {t("pricing.credits.costImage", { defaultValue: "Gambar / Foto: 1 kredit" })}
              </div>
              <div className="rounded-sm border border-border bg-secondary/20 px-3 py-2 text-xs text-muted-foreground">
                {t("pricing.credits.costAudio", { defaultValue: "Audio: 2 kredit" })}
              </div>
              <div className="rounded-sm border border-border bg-secondary/20 px-3 py-2 text-xs text-muted-foreground">
                {t("pricing.credits.costVideo", { defaultValue: "Video: 3 kredit" })}
              </div>
            </div>

            {creditPacksLoading ? (
              <div className="mt-4 rounded-sm border border-border bg-card p-4 text-sm text-muted-foreground">
                {t("common.loading")}
              </div>
            ) : (
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                {creditPacks.map((pack) => {
                  const isBusy = buyingPackId === pack.id;
                  return (
                    <div key={pack.id} className="rounded-sm border border-border bg-card p-4">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-heading text-base font-semibold text-foreground">
                          {pack.label}
                        </p>
                        {pack.is_most_used && (
                          <span className="text-[10px] font-mono uppercase px-2 py-1 rounded-sm border border-brand-primary/30 bg-brand-primary/10 text-brand-primary">
                            {t("pricing.credits.badgePopular", { defaultValue: "Terpopuler" })}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        {t("pricing.credits.amount", {
                          defaultValue: "{{count}} kredit",
                          count: Number(pack.credits || 0).toLocaleString(locale),
                        })}
                      </p>
                      <p className="font-heading text-lg font-semibold mt-1">
                        Rp {Number(pack.price_idr || 0).toLocaleString(locale)}
                      </p>
                      <Button
                        className="mt-4 w-full rounded-sm gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white"
                        onClick={() => handleBuyCredits(pack.id)}
                        disabled={isBusy}
                        data-testid={`pricing-credit-buy-${pack.id}`}
                      >
                        {isBusy ? (
                          t("common.loading")
                        ) : (
                          <>
                            <CreditCard className="w-3.5 h-3.5" />
                            {t("dashboard.credits.buy")}
                          </>
                        )}
                      </Button>
                    </div>
                  );
                })}
                {!creditPacks.length && (
                  <div className="rounded-sm border border-border bg-card p-4 text-sm text-muted-foreground md:col-span-3">
                    {t("pricing.credits.empty", {
                      defaultValue: "Paket kredit belum tersedia saat ini.",
                    })}
                  </div>
                )}
              </div>
            )}
            <div className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground">
              <Check className="w-3.5 h-3.5 text-verdict-real" />
              {t("pricing.credits.noExpire", { defaultValue: "Kredit tidak kedaluwarsa." })}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
